import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SentimentTag } from "@/components/SellerRep";
import ProductCard from "@/components/ProductCard";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { Store, ShoppingBag, CalendarDays, ShieldCheck, ThumbsUp, ThumbsDown, Heart, Lock } from "lucide-react";
import Breadcrumb from "@/components/Breadcrumb";
import { useSeo } from "@/lib/seo";

const fmtMonth = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export default function Seller() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useSeo(profile ? {
    title: `${profile.name} — Your Classic Style`,
    description: `Shop ${profile.name}'s closet on Your Classic Style — curated classic & vintage pieces.`,
  } : {});

  const loadProfile = () => api.get(`/sellers/${id}`).then((r) => setProfile(r.data)).catch(() => setNotFound(true));
  useEffect(() => {
    loadProfile();
    api.get(`/sellers/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  const toggleFavorite = async () => {
    if (!user) { nav("/auth"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/sellers/${id}/favorite`);
      setProfile((p) => ({ ...p, favorited: data.favorited }));
      toast.success(data.favorited ? "Added to your favourite closets" : "Removed from favourites");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const privateAction = async () => {
    if (!user) { nav("/auth"); return; }
    const status = profile.private_status;
    if (status === "active" || status === "owner") { nav(`/seller/${id}/private`); return; }
    if (status === "pending") { toast.info("Your request is pending the seller's approval"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/sellers/${id}/private/request`);
      setProfile((p) => ({ ...p, private_status: data.status }));
      if (data.status === "active") nav(`/seller/${id}/private`);
      else toast.success("Request sent — the seller will review it shortly");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="seller-notfound">
        <div className="label-eyebrow">Seller</div>
        <h1 className="font-display text-3xl mt-2">This closet isn't available.</h1>
        <Link to="/browse" className="btn-primary inline-flex mt-6">Browse the marketplace</Link>
      </div>
    );
  }
  if (!profile) return <div className="p-16 text-center label-eyebrow" data-testid="seller-loading">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10" data-testid="seller-page">
      <Breadcrumb className="mb-5" items={[{ label: "Home", to: "/" }, { label: profile.name }]} />
      <div className="hairline-b pb-8">
        <div className="flex items-center gap-3">
          {profile.avatar
            ? <Avatar src={profile.avatar} name={profile.name} size={48} testid="seller-avatar" />
            : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "#1E3A2B" }}>
                <Store size={20} style={{ color: "#F9F6F0" }} />
              </div>
            )}
          <div>
            <div className="label-eyebrow">Seller closet</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter" data-testid="seller-name">{profile.name}</h1>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="seller-reputation">
          <div className="hairline p-4" data-testid="rep-sold">
            <div className="flex items-center gap-1.5 text-neutral-500"><ShoppingBag size={14} /> <span className="label-eyebrow">Items sold</span></div>
            <div className="font-display text-3xl mt-1">{profile.sales}</div>
          </div>
          <div className="hairline p-4" data-testid="rep-good">
            <div className="flex items-center gap-1.5" style={{ color: "#16A34A" }}><ThumbsUp size={14} /> <span className="label-eyebrow">Good</span></div>
            <div className="font-display text-3xl mt-1">{profile.good || 0}</div>
          </div>
          <div className="hairline p-4" data-testid="rep-bad">
            <div className="flex items-center gap-1.5" style={{ color: "#E11D48" }}><ThumbsDown size={14} /> <span className="label-eyebrow">Bad</span></div>
            <div className="font-display text-3xl mt-1">{profile.bad || 0}</div>
          </div>
          <div className="hairline p-4" data-testid="rep-verified">
            <div className="flex items-center gap-1.5" style={{ color: "#2563EB" }}><ShieldCheck size={14} /> <span className="label-eyebrow">Verified authentic</span></div>
            <div className="font-display text-3xl mt-1">{profile.verified_sales || 0}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-500">
          <span className="flex items-center gap-2" data-testid="seller-joined"><CalendarDays size={15} /> Joined {fmtMonth(profile.joined)}</span>
          {profile.review_count > 0 && (
            <span data-testid="seller-review-count">{profile.review_count} buyer review{profile.review_count === 1 ? "" : "s"}</span>
          )}
        </div>
        {profile.private_status !== "owner" && (
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={toggleFavorite} disabled={busy} data-testid="favorite-seller-btn"
              className="btn-outline text-[0.68rem]"
              style={profile.favorited ? { background: "#1E3A2B", color: "#fff", borderColor: "#1E3A2B" } : {}}>
              <Heart size={13} fill={profile.favorited ? "#fff" : "none"} />
              {profile.favorited ? "Favourited" : "Favourite"}
            </button>
            {profile.private_count > 0 && (
              <button onClick={privateAction} disabled={busy} data-testid="private-listing-btn"
                className="btn-outline text-[0.68rem]"
                style={profile.private_status === "active" ? { background: "#1E3A2B", color: "#fff", borderColor: "#1E3A2B" } : {}}>
                <Lock size={13} />
                {profile.private_status === "active" ? "View private listing"
                  : profile.private_status === "pending" ? "Request pending"
                  : "Private listing"}
              </button>
            )}
          </div>
        )}
        {profile.private_status === "owner" && profile.private_count > 0 && (
          <div className="mt-6">
            <Link to={`/seller/${id}/private`} className="btn-outline text-[0.68rem]" data-testid="owner-view-private-btn">
              <Lock size={13} /> Your private listing ({profile.private_count})
            </Link>
          </div>
        )}
      </div>

      <section className="mt-10">
        <div className="hairline-b pb-3 flex items-baseline justify-between">
          <h2 className="font-display text-2xl">In this closet</h2>
          <span className="label-eyebrow">{profile.listings.length} item{profile.listings.length === 1 ? "" : "s"}</span>
        </div>
        {profile.listings.length === 0 ? (
          <div className="hairline p-14 text-center mt-6 text-sm text-neutral-500" data-testid="seller-no-listings">
            No active listings right now — check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6" data-testid="seller-listings">
            {profile.listings.map((it) => (
              <ProductCard key={it.id} item={it} testid={`seller-item-${it.id}`} />
            ))}
          </div>
        )}
      </section>

      {reviews && reviews.count > 0 && (
        <section className="mt-14" data-testid="seller-reviews">
          <div className="hairline-b pb-3">
            <h2 className="font-display text-2xl">Buyer reviews</h2>
          </div>
          <ul className="mt-6 grid md:grid-cols-2 gap-4">
            {reviews.reviews.map((rv, i) => (
              <li key={`${rv.created_at || "rv"}-${rv.buyer_name || i}`} className="hairline p-5 bg-white" data-testid={`seller-review-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <SentimentTag sentiment={rv.sentiment} />
                  <span className="text-[0.62rem] text-neutral-400">{fmtDate(rv.created_at)}</span>
                </div>
                {rv.comment && <p className="mt-2 text-sm text-neutral-700 leading-relaxed">“{rv.comment}”</p>}
                <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
                  <Avatar src={rv.buyer_avatar} name={rv.buyer_name} size={22} />
                  <span>{rv.buyer_name}{rv.item_title ? ` · ${rv.item_title}` : ""}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
